import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { AlertTriangle, FileX, RotateCcw, XCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { FinanceBulkImportRowStatus } from '@/graphql/generatedTypes';
import { allUserCountriesQuery } from '@/graphql/queries';
import {
  useApplyFinanceBulkImportMutation,
  useValidateFinanceBulkImportMutation,
} from './financeBulkImport.generated';
import {
  decodeCsvBytes,
  downloadCsv,
  normalizeHeader,
  parseCsv,
  serializeCsv,
} from './csv';
import {
  ImportFile,
  ImportResult,
  cellValue,
  errorRowsCsvData,
  resultCsvData,
  toApplyInputs,
  toRowInputs,
  withCellValue,
} from './importModel';
import { EditingCell } from './ImportTable';
import { ResultPanel } from './ResultPanel';
import { ReviewFilter, ReviewPanel } from './ReviewPanel';
import { TemplateCard } from './TemplateCard';
import { UploadCard } from './UploadCard';

/** Edits are validated together once the user pauses this long. */
const REVALIDATE_DELAY_MS = 500;

type Step = 'upload' | 'review' | 'result';

const baseName = (fileName: string) => fileName.replace(/\.csv$/i, '');

export default function FinanceBulkImportPage() {
  const [step, setStep] = useState<Step>('upload');
  const [file, setFileState] = useState<ImportFile | null>(null);
  const [validation, setValidation] = useState<ImportResult | null>(null);
  const [validatedFile, setValidatedFile] = useState<ImportFile | null>(null);
  const [applyResult, setApplyResult] = useState<ImportResult | null>(null);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [emptyFileName, setEmptyFileName] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [filter, setFilter] = useState<ReviewFilter>('changes');
  const [editing, setEditing] = useState<EditingCell>(null);

  // The latest file, for the debounced validation, and the latest request,
  // so a slower earlier response never overwrites a newer one.
  const fileRef = useRef<ImportFile | null>(null);
  const requestSeq = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [validate] = useValidateFinanceBulkImportMutation();
  const [apply, { loading: applying }] = useApplyFinanceBulkImportMutation();
  const { data: countryData } = useQuery(allUserCountriesQuery);
  const countries: string[] = (countryData?.__type?.enumValues ?? []).map(
    (value: { name: string }) => value.name,
  );

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const setFile = (next: ImportFile | null) => {
    fileRef.current = next;
    setFileState(next);
  };

  const cancelScheduled = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };

  /**
   * Validates the whole row set. `openReview` switches to the review step and
   * picks the default tab (after an upload or "Validate again").
   */
  const runValidation = async (target: ImportFile, openReview: boolean) => {
    const seq = ++requestSeq.current;
    setValidating(true);
    let data: ImportResult;
    try {
      const response = await validate({
        variables: { columns: target.columns, rows: toRowInputs(target) },
      });
      data = response.data!.validateFinanceBulkImport;
    } catch (error) {
      if (seq === requestSeq.current) {
        setValidating(false);
        setDirty(true);
        setRequestError((error as Error).message);
      }
      return;
    }
    if (seq !== requestSeq.current) {
      return;
    }
    setValidating(false);
    setRequestError(null);
    setFileErrors(data.file_errors);
    if (data.file_errors.length > 0) {
      setDirty(true);
      return;
    }
    setValidation(data);
    setValidatedFile(target);
    setDirty(fileRef.current !== target);
    if (openReview) {
      const hasErrors = data.rows.some(
        (row) => row.status === FinanceBulkImportRowStatus.Error,
      );
      setFilter(hasErrors ? 'errors' : 'changes');
      setApplyResult(null);
      setStep('review');
    }
  };

  const scheduleValidation = (next: ImportFile) => {
    setFile(next);
    setDirty(true);
    cancelScheduled();
    if (next.rows.length === 0) {
      requestSeq.current += 1;
      setValidating(false);
      return;
    }
    timer.current = setTimeout(() => {
      timer.current = null;
      if (fileRef.current) {
        void runValidation(fileRef.current, false);
      }
    }, REVALIDATE_DELAY_MS);
  };

  const reset = () => {
    cancelScheduled();
    requestSeq.current += 1;
    setStep('upload');
    setFile(null);
    setValidation(null);
    setValidatedFile(null);
    setApplyResult(null);
    setFileErrors([]);
    setEmptyFileName(null);
    setRequestError(null);
    setValidating(false);
    setDirty(false);
    setEditing(null);
  };

  const onFile = async (upload: File) => {
    reset();
    let text: string;
    try {
      text = decodeCsvBytes(await upload.arrayBuffer());
    } catch (error) {
      setRequestError(`Could not read the file: ${(error as Error).message}`);
      return;
    }
    const parsed = parseCsv(text);
    if (!parsed || parsed.records.length === 0) {
      setEmptyFileName(upload.name);
      return;
    }
    const next: ImportFile = {
      fileName: upload.name,
      separator: parsed.separator,
      columns: parsed.header.map(normalizeHeader),
      rows: parsed.records.map((record) => ({
        rowNumber: record.rowNumber,
        values: record.values,
      })),
    };
    setFile(next);
    await runValidation(next, true);
  };

  const onCommit = (rowNumber: number, column: string, value: string) => {
    setEditing(null);
    const current = fileRef.current;
    const row = current?.rows.find((r) => r.rowNumber === rowNumber);
    if (!current || !row || cellValue(current, row, column) === value) {
      return;
    }
    scheduleValidation({
      ...current,
      rows: current.rows.map((r) =>
        r.rowNumber === rowNumber
          ? withCellValue(current, r, column, value)
          : r,
      ),
    });
  };

  const onRemove = (rowNumber: number) => {
    const current = fileRef.current;
    if (!current) return;
    if (editing?.rowNumber === rowNumber) setEditing(null);
    scheduleValidation({
      ...current,
      rows: current.rows.filter((r) => r.rowNumber !== rowNumber),
    });
  };

  const onApply = async () => {
    if (!validatedFile || !validation) return;
    try {
      const response = await apply({
        variables: {
          columns: validatedFile.columns,
          rows: toApplyInputs(validatedFile, validation),
        },
      });
      const data = response.data!.applyFinanceBulkImport;
      if (data.file_errors.length > 0) {
        setFileErrors(data.file_errors);
        return;
      }
      setRequestError(null);
      setApplyResult(data);
      setStep('result');
    } catch (error) {
      setRequestError((error as Error).message);
    }
  };

  const onDownloadErrors = () => {
    if (!file || !validation || !validatedFile) return;
    const { header, rows } = errorRowsCsvData(validatedFile, validation);
    downloadCsv(
      `${baseName(file.fileName)}-errors.csv`,
      serializeCsv(header, rows, file.separator),
    );
  };

  const onDownloadResult = () => {
    if (!file || !applyResult) return;
    const { header, rows } = resultCsvData(applyResult);
    downloadCsv(
      `${baseName(file.fileName)}-result.csv`,
      serializeCsv(header, rows, file.separator),
    );
  };

  const ignoredColumns = validation?.ignored_columns ?? [];

  return (
    <div className="tw:mx-auto tw:flex tw:max-w-[1600px] tw:flex-col tw:gap-6 tw:p-6">
      <div className="tw:flex tw:flex-col tw:gap-1">
        <h2 className="tw:text-2xl tw:font-semibold">Finance bulk import</h2>
        <p className="tw:text-muted-foreground tw:text-sm">
          Update VAT numbers, payment terms, SEPA and collection flags, dunning
          blocks and invoice countries of many stores and invoice collections
          from one CSV file.
        </p>
      </div>

      {requestError && (
        <Alert variant="destructive" data-testid="request-error">
          <XCircle />
          <AlertTitle>The request failed</AlertTitle>
          <AlertDescription>
            <p>{requestError}</p>
            <div className="tw:mt-2 tw:flex tw:gap-2">
              {step !== 'upload' && dirty && file && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void runValidation(file, false)}
                >
                  Retry validation
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setRequestError(null)}
              >
                Dismiss
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {fileErrors.length > 0 && (
        <Alert variant="destructive" data-testid="file-errors">
          <FileX />
          <AlertTitle>The file cannot be imported</AlertTitle>
          <AlertDescription>
            <ul className="tw:list-disc tw:pl-5">
              {fileErrors.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {step === 'upload' && (
        <>
          <TemplateCard />
          {emptyFileName ? (
            <Empty className="tw:border" data-testid="empty-file">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <FileX />
                </EmptyMedia>
                <EmptyTitle>No data rows</EmptyTitle>
                <EmptyDescription>
                  {emptyFileName} has no rows below the header. Fill in at least
                  one row and upload it again.
                </EmptyDescription>
              </EmptyHeader>
              <EmptyContent>
                <Button variant="outline" onClick={reset}>
                  Choose another file
                </Button>
              </EmptyContent>
            </Empty>
          ) : (
            <UploadCard busy={validating} onFile={(f) => void onFile(f)} />
          )}
        </>
      )}

      {step !== 'upload' && ignoredColumns.length > 0 && (
        <Alert data-testid="ignored-columns">
          <AlertTriangle />
          <AlertTitle>Columns not imported</AlertTitle>
          <AlertDescription>
            The import does not know these columns and ignores them:{' '}
            {ignoredColumns.join(', ')}.
          </AlertDescription>
        </Alert>
      )}

      {step === 'review' &&
        file &&
        validation &&
        validatedFile &&
        (file.rows.length === 0 ? (
          <Empty className="tw:border">
            <EmptyHeader>
              <EmptyTitle>All rows removed</EmptyTitle>
              <EmptyDescription>
                There is nothing left to import.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button variant="outline" onClick={reset}>
                <RotateCcw />
                Start new import
              </Button>
            </EmptyContent>
          </Empty>
        ) : (
          <ReviewPanel
            file={file}
            validation={validation}
            validatedFile={validatedFile}
            countries={countries}
            validating={validating}
            dirty={dirty}
            applying={applying}
            filter={filter}
            onFilterChange={setFilter}
            editing={editing}
            onEditingChange={setEditing}
            onCommit={onCommit}
            onRemove={onRemove}
            onDownloadErrors={onDownloadErrors}
            onApply={() => void onApply()}
            onReset={reset}
          />
        ))}

      {step === 'result' && file && applyResult && (
        <ResultPanel
          file={file}
          result={applyResult}
          validating={validating}
          onValidateAgain={() =>
            fileRef.current && void runValidation(fileRef.current, true)
          }
          onDownloadResult={onDownloadResult}
          onReset={reset}
        />
      )}
    </div>
  );
}
